import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiLock, FiLogOut } from 'react-icons/fi';
import {
  clearClientPortalToken,
  getClientPortalMe,
  getClientPortalToken,
  getClientPurchaseOrderById,
  getClientPurchaseOrderCatalog,
  submitClientPurchaseOrder,
  updateClientPurchaseOrder,
} from '../api/clientPortalApi';
import ClientPortalWorkflowNav from '../components/ClientPortalWorkflowNav';
import DocumentHeader from '../components/DocumentHeader';
import InlineLoading from '../components/InlineLoading';
import PurchaseOrderCatalogEditor from '../components/PurchaseOrderCatalogEditor';
import { notify } from '../utils/alertBus';
import { isPurchaseOrderPreparingStatus } from '../utils/purchaseOrderStatus';
import {
  startClientCatalogEvents,
  stopClientCatalogEvents,
  subscribeClientCatalogEvents,
} from '../utils/clientCatalogEventsHub';
import usePromotionClock from '../hooks/usePromotionClock';

const isOrderEditableByClient = (order) => (
  order
  && !order.purchaseId
  && isPurchaseOrderPreparingStatus(order.status)
);

const ClientPurchaseOrderEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const routeScope = isEditMode ? `edit:${String(id)}` : 'new';
  const [client, setClient] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [promotionClock, setPromotionClock] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolvedRouteScope, setResolvedRouteScope] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [catalogUpdated, setCatalogUpdated] = useState(false);
  const catalogRefreshTimerRef = useRef(null);
  const catalogRequestRef = useRef(0);
  const loadGenerationRef = useRef(0);
  const routeScopeRef = useRef(routeScope);
  const loadedRouteScopeRef = useRef(null);
  routeScopeRef.current = routeScope;

  const load = useCallback(async () => {
    const requestGeneration = loadGenerationRef.current + 1;
    const requestScope = routeScope;
    loadGenerationRef.current = requestGeneration;
    loadedRouteScopeRef.current = null;
    const isCurrentRequest = () => (
      loadGenerationRef.current === requestGeneration
      && routeScopeRef.current === requestScope
    );
    const catalogRequestId = catalogRequestRef.current + 1;
    catalogRequestRef.current = catalogRequestId;

    setClient(null);
    setCatalog([]);
    setPromotionClock(null);
    setOrder(null);
    if (!getClientPortalToken()) {
      if (isCurrentRequest()) navigate('/', { replace: true });
      return;
    }
    setLoading(true);
    setResolvedRouteScope(null);
    setError('');
    setSuccessMessage('');
    setCatalogUpdated(false);
    try {
      const me = await getClientPortalMe();
      if (!isCurrentRequest()) return;
      if (me?.mustChangePassword) {
        navigate('/client-portal/change-password', { replace: true });
        return;
      }
      const [catalogResponse, orderResponse] = await Promise.all([
        getClientPurchaseOrderCatalog(),
        isEditMode ? getClientPurchaseOrderById(id) : Promise.resolve(null),
      ]);
      if (!isCurrentRequest()) return;
      setClient(me);
      if (catalogRequestRef.current === catalogRequestId) {
        setCatalog(Array.isArray(catalogResponse?.products) ? catalogResponse.products : []);
        setPromotionClock(catalogResponse?.promotionClock || null);
      }
      setOrder(orderResponse);
      loadedRouteScopeRef.current = requestScope;
      setResolvedRouteScope(requestScope);
      if (isEditMode && !isOrderEditableByClient(orderResponse)) {
        setError('Esta orden ya no puede ser editada.');
      }
    } catch (requestError) {
      if (!isCurrentRequest()) return;
      setResolvedRouteScope(requestScope);
      const code = requestError?.response?.data?.code;
      if (code === 'PASSWORD_CHANGE_REQUIRED') {
        navigate('/client-portal/change-password', { replace: true });
        return;
      }
      if (requestError?.response?.status === 401) {
        clearClientPortalToken();
        navigate('/', { replace: true });
        return;
      }
      setError(requestError?.response?.data?.message || 'No se pudo cargar la orden.');
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  }, [id, isEditMode, navigate, routeScope]);

  useEffect(() => {
    load();
    return () => {
      loadGenerationRef.current += 1;
      catalogRequestRef.current += 1;
      loadedRouteScopeRef.current = null;
    };
  }, [load]);

  const refreshCatalog = useCallback(async () => {
    const requestGeneration = loadGenerationRef.current;
    const requestScope = routeScope;
    if (
      routeScopeRef.current !== requestScope
      || loadedRouteScopeRef.current !== requestScope
      || !getClientPortalToken()
    ) return;
    const requestId = catalogRequestRef.current + 1;
    catalogRequestRef.current = requestId;
    const isCurrentRequest = () => (
      catalogRequestRef.current === requestId
      && loadGenerationRef.current === requestGeneration
      && routeScopeRef.current === requestScope
      && loadedRouteScopeRef.current === requestScope
    );
    try {
      const response = await getClientPurchaseOrderCatalog();
      if (!isCurrentRequest()) return;
      setCatalog(Array.isArray(response?.products) ? response.products : []);
      setPromotionClock(response?.promotionClock || null);
      setCatalogUpdated(true);
    } catch (requestError) {
      if (!isCurrentRequest()) return;
      setError(requestError?.response?.data?.message || 'No se pudo actualizar el catalogo.');
    }
  }, [routeScope]);

  const promotionBoundary = useMemo(() => (
    promotionClock?.nextBoundaryAt
      ? [{ startsAt: promotionClock.nextBoundaryAt }]
      : []
  ), [promotionClock?.nextBoundaryAt]);
  usePromotionClock(promotionBoundary, refreshCatalog, promotionClock);

  useEffect(() => {
    if (!getClientPortalToken()) return undefined;
    const unsubscribe = subscribeClientCatalogEvents(() => {
      if (catalogRefreshTimerRef.current) clearTimeout(catalogRefreshTimerRef.current);
      catalogRefreshTimerRef.current = setTimeout(refreshCatalog, 250);
    });
    startClientCatalogEvents();
    return () => {
      unsubscribe();
      stopClientCatalogEvents();
      if (catalogRefreshTimerRef.current) clearTimeout(catalogRefreshTimerRef.current);
      catalogRefreshTimerRef.current = null;
    };
  }, [refreshCatalog]);

  const handleLogout = () => {
    loadGenerationRef.current += 1;
    catalogRequestRef.current += 1;
    loadedRouteScopeRef.current = null;
    stopClientCatalogEvents();
    clearClientPortalToken();
    navigate('/', { replace: true });
  };

  const handleSubmit = async ({ items, notes, resetEditor }) => {
    const submitGeneration = loadGenerationRef.current;
    const submitScope = routeScope;
    const isCurrentSubmission = () => (
      loadGenerationRef.current === submitGeneration
      && routeScopeRef.current === submitScope
      && loadedRouteScopeRef.current === submitScope
    );
    const orderMatchesRoute = !isEditMode || String(order?.id) === String(id);
    if (!isCurrentSubmission() || resolvedRouteScope !== submitScope || !orderMatchesRoute) {
      const staleRouteError = new Error('La orden cambió mientras se cargaba. Intenta nuevamente.');
      staleRouteError.response = { data: { message: staleRouteError.message } };
      throw staleRouteError;
    }
    setSuccessMessage('');
    if (isEditMode) {
      await updateClientPurchaseOrder(id, { items, notes });
      if (!isCurrentSubmission()) return;
      notify('Orden de compra actualizada.');
      navigate('/client-portal/purchase-orders');
      return;
    }
    const response = await submitClientPurchaseOrder({ items, notes });
    if (!isCurrentSubmission()) return;
    const requestId = response?.purchaseOrder?.id;
    resetEditor();
    setSuccessMessage(`Orden de compra enviada. Numero: #${requestId || ''}`);
  };

  const topContent = useMemo(() => (
    <ClientPortalWorkflowNav
      activeKey="purchase-order"
      onNavigate={navigate}
      className="mb-4"
    />
  ), [navigate]);

  const successContent = successMessage ? (
    <div>
      <p className="text-sm font-semibold text-gray-900">Orden guardada</p>
      <p className="mt-0.5 text-sm text-emerald-700">{successMessage}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate('/client-portal/purchase-orders')}
          className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          Ver mis ordenes
        </button>
        <button
          type="button"
          onClick={() => setSuccessMessage('')}
          className="rounded border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Crear otra
        </button>
      </div>
    </div>
  ) : null;

  if (loading || resolvedRouteScope !== routeScope) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <InlineLoading label="Cargando orden" />
      </div>
    );
  }

  const blockedEdit = isEditMode && (!order || !isOrderEditableByClient(order));

  return (
    <div className="flex min-h-screen flex-col bg-gray-100">
      <DocumentHeader
        stickyOffsetClassName="top-0"
        title={isEditMode ? `Editar orden #${id}` : 'Crear orden de compra'}
        showCartButton={false}
        headerActions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/client-portal/change-password')}
              className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FiLock />
              Contraseña
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FiLogOut />
              Salir
            </button>
          </div>
        }
        clientName={client?.name || ''}
        clientRut={client?.rut || 'Cliente'}
        showClientType={false}
        emptyClientLabel={client?.name || 'Cliente'}
      />

      {blockedEdit ? (
        <main className="mx-auto w-full max-w-3xl p-4">
          {topContent}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {error || 'Esta orden ya no puede ser editada.'}
            <button
              type="button"
              onClick={() => navigate('/client-portal/purchase-orders')}
              className="mt-3 block rounded bg-amber-700 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-800"
            >
              Volver a mis ordenes
            </button>
          </div>
        </main>
      ) : (
        <>
        {catalogUpdated && (
          <div className="mx-auto mb-3 w-full max-w-7xl rounded border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">
            Los precios y descuentos del catalogo fueron actualizados.
          </div>
        )}
        <PurchaseOrderCatalogEditor
          catalog={catalog}
          promotionClock={promotionClock}
          initialItems={isEditMode ? order?.Items || [] : []}
          initialNotes={isEditMode ? order?.notes || '' : ''}
          resetKey={isEditMode ? `edit-${order?.id}-${order?.updatedAt}` : `new-${successMessage}`}
          submitLabel={isEditMode ? 'Guardar cambios' : 'Enviar orden de compra'}
          cartDescription="Los totales se muestran antes de guardar."
          notesPlaceholder="Notas para la orden (opcional)"
          onSubmit={handleSubmit}
          topContent={topContent}
          successContent={successContent}
        />
        </>
      )}
    </div>
  );
};

export default ClientPurchaseOrderEditorPage;
